import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmDialog, errorList } from './shared';
import { makeThumb } from '../../utils/imageThumb';

/*
 * As imagens e os vídeos de um exercício (ou as imagens de uma máquina): ver, carregar, validar, escolher a principal, mudar o
 * texto alternativo e apagar. Fala com a API da biblioteca (o servidor valida tamanho, tipo, conteúdo e duração); aqui só se
 * evita enviar o que se sabe que vai ser recusado.
 *
 * Um ficheiro que o ginásio carrega tem origem e licença visíveis; o que vem de terceiros (aberto/licenciado) exige licença,
 * atribuição e o endereço de origem. Um vídeo entra PENDENTE: só chega aos sócios depois de uma pessoa o aprovar.
 *
 * Props:
 *   api       exerciseLibrary | equipment  (de services/api)
 *   ownerId   id do exercício/máquina (já gravado)
 *   media     lista actual
 *   limits    { maxBytes, maxPerItem, extensions } do /meta
 *   onChange  (novaLista) => void   — a lista actual depois de cada operação
 *   demo      { inUse, inUseLabel, reason, … } — qual demonstração está em uso e porquê (só exercícios)
 *   onDetail  (detalhe) => void     — o exercício inteiro devolvido pelo servidor (embed) para o ecrã refrescar
 *   canEdit   falso → só mostra
 */

const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp,image/gif';
// `video/*` faz o telemóvel oferecer a câmara e a galeria; o servidor só aceita MP4 (H.264) e WEBM, e é isso
// que o ecrã confirma antes de enviar (um `.mov` do iPhone é recusado com uma mensagem clara, não em silêncio).
const ACCEPT_ALL = `${ACCEPT_IMAGES},video/*`;
// Os tectos que o servidor aplica (LibraryMediaService): MP4 30 MB, WEBM 15 MB, ≤30 s, ≥240 px de altura.
const MAX_VIDEO_BYTES = 30 * 1024 * 1024;
const MAX_WEBM_BYTES = 15 * 1024 * 1024;
const MAX_VIDEO_SECONDS = 30;
const MIN_VIDEO_HEIGHT = 240;

const ORIGINS = [
  ['upload', 'Carregado pelo ginásio'],
  ['own', 'Produção própria'],
  ['open', 'Licença aberta / domínio público'],
  ['licensed', 'Licenciado a um fornecedor'],
  ['ai', 'Gerado por IA'],
];

const isVideoFile = (file) => /^video\//.test(file?.type || '') || /\.(mp4|webm|mov|m4v)$/i.test(file?.name || '');
const mb = (bytes) => `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;

// A forma dos endereços do YouTube que a biblioteca aceita. Isto é só para não incomodar o servidor com o que se vê
// logo que está errado: quem decide é o servidor (extrai o id e recusa outro domínio).
const LOOKS_LIKE_YOUTUBE = /^https?:\/\/(www\.|m\.|music\.)?(youtube\.com\/(watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)/i;

/** O erro real do envio, como o servidor o descreve — e, quando o corpo é grande demais, o motivo é o tamanho. */
const uploadError = (error, video) => {
  const status = error?.response?.status;
  if (status === 413) {
    return video
      ? 'O vídeo é maior do que o servidor aceita (MP4 até 30 MB, WEBM até 15 MB). Encurte ou comprima a gravação.'
      : 'O ficheiro é maior do que o servidor aceita (10 MB por imagem).';
  }
  if (status === 400 && video) {
    const said = errorList(error, '').join(' ');
    if (said) return said;   // ex.: duração > 30 s, altura < 240 px, MP4 ilegível
  }
  return errorList(error, video ? 'Não foi possível carregar o vídeo.' : 'Não foi possível carregar o ficheiro.').join(' ');
};

export default function MediaManager({ api, ownerId, media, limits, onChange, demo = null, onDetail = null, canEdit = true }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [alt, setAlt] = useState('');
  const [makePrimary, setMakePrimary] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toRemoveEmbed, setToRemoveEmbed] = useState(false);
  const [message, setMessage] = useState(null);
  const [origin, setOrigin] = useState('upload');
  const [license, setLicense] = useState('');
  const [attribution, setAttribution] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [review, setReview] = useState(null);   // { item, decision, note }
  const [showProvenance, setShowProvenance] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedChannel, setEmbedChannel] = useState('');

  const supportsVideo = typeof api?.reviewMedia === 'function';   // só os exercícios têm vídeo e validação
  const supportsEmbed = typeof api?.addEmbed === 'function';      // e o vídeo incorporado do YouTube
  const embed = (media || []).find((m) => m.kind === 'embed') || null;
  const maxBytes = limits?.maxBytes ?? 10 * 1024 * 1024;
  const maxPerItem = limits?.maxPerItem ?? 10;
  const full = media.length >= maxPerItem;
  const thirdParty = origin === 'open' || origin === 'licensed';

  const refreshWith = (next) => onChange(next.sort((a, b) => (b.isPrimary - a.isPrimary) || (a.sortOrder - b.sortOrder) || (a.id - b.id)));

  const upload = async (file) => {
    setMessage(null);
    if (!file) return;
    const video = isVideoFile(file);
    const webm = /\.webm$/i.test(file.name || '') || file.type === 'video/webm';
    const mp4 = /\.mp4$/i.test(file.name || '') || file.type === 'video/mp4';
    const limit = video ? (webm ? MAX_WEBM_BYTES : MAX_VIDEO_BYTES) : maxBytes;
    if (video && !supportsVideo) {
      setMessage('Só os exercícios têm vídeo.');
      return;
    }
    if (video && !mp4 && !webm) {
      setMessage('Vídeo em formato não suportado. Use MP4 (H.264) ou WEBM: na câmara do telemóvel, exporte/escolha MP4.');
      return;
    }
    if (file.size > limit) {
      setMessage(`O ficheiro tem ${mb(file.size)}; o máximo é ${Math.floor(limit / 1024 / 1024)} MB${video ? ` (MP4 até 30 MB, WEBM até 15 MB)` : ''}.`);
      return;
    }
    if (thirdParty && (!license.trim() || !attribution.trim() || !/^https:\/\//i.test(sourceUrl.trim()))) {
      setMessage('Conteúdo aberto ou licenciado precisa de licença, atribuição e o endereço de origem (https://…).');
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const thumb = video ? null : await makeThumb(file);
      const { data } = await api.addMedia(ownerId, file, {
        altText: alt.trim() || undefined, makePrimary: !video && makePrimary,
        origin: origin !== 'upload' ? origin : undefined,
        license: license.trim() || undefined, attribution: attribution.trim() || undefined, sourceUrl: sourceUrl.trim() || undefined,
        thumb: thumb || undefined,
        // A percentagem só aparece quando o navegador a sabe (não em envios minúsculos).
        onProgress: (e) => { if (e?.total) setProgress(Math.min(100, Math.round((e.loaded / e.total) * 100))); },
      });
      const others = data.isPrimary ? media.map((m) => ({ ...m, isPrimary: false })) : media;
      refreshWith([...others, data]);
      setAlt(''); setMakePrimary(false); setLicense(''); setAttribution(''); setSourceUrl(''); setOrigin('upload');
      toast.success(video ? 'Vídeo carregado: fica pendente até ser aprovado' : 'Imagem carregada');
    } catch (e) {
      setMessage(uploadError(e, video));
    } finally {
      setBusy(false);
      setProgress(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const setPrimary = async (item) => {
    setBusy(true);
    setMessage(null);
    try {
      await api.updateMedia(ownerId, item.id, { isPrimary: true });
      refreshWith(media.map((m) => ({ ...m, isPrimary: m.id === item.id })));
    } catch (e) {
      setMessage(errorList(e).join(' '));
    } finally {
      setBusy(false);
    }
  };

  const saveAlt = async (item, value) => {
    if ((item.altText || '') === value.trim()) return;
    setMessage(null);
    try {
      const { data } = await api.updateMedia(ownerId, item.id, { altText: value.trim() });
      onChange(media.map((m) => (m.id === item.id ? { ...m, altText: data.altText } : m)));
    } catch (e) {
      setMessage(errorList(e).join(' '));
    }
  };

  const remove = async () => {
    const item = toDelete;
    setBusy(true);
    try {
      await api.removeMedia(ownerId, item.id);
      let next = media.filter((m) => m.id !== item.id);
      // O servidor promove a seguinte a principal quando se apaga a principal.
      if (item.isPrimary && next.length > 0 && !next.some((m) => m.isPrimary)) {
        const first = [...next].filter((m) => m.kind === 'photo').sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id))[0];
        next = next.map((m) => ({ ...m, isPrimary: first ? m.id === first.id : false }));
      }
      refreshWith(next);
      toast.success('Ficheiro apagado');
    } catch (e) {
      setMessage(errorList(e).join(' '));
    } finally {
      setBusy(false);
      setToDelete(null);
    }
  };

  const submitReview = async () => {
    const { item, decision, note } = review;
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await api.reviewMedia(ownerId, item.id, decision, note);
      // Uma principal que deixa de estar aprovada passa a vez à primeira imagem aprovada (a mesma regra do servidor).
      let next = media.map((m) => (m.id === item.id ? { ...m, ...data } : m));
      if (item.isPrimary && data.validationStatus !== 'approved') {
        const first = next.filter((m) => m.kind === 'photo' && m.validationStatus === 'approved' && m.id !== item.id)
          .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id))[0];
        next = next.map((m) => ({ ...m, isPrimary: first ? m.id === first.id : false }));
      }
      refreshWith(next);
      setReview(null);
      toast.success(decision === 'approve' ? 'Aprovado: já aparece aos atletas' : decision === 'reject' ? 'Rejeitado' : 'Voltou a pendente');
    } catch (e) {
      setMessage(errorList(e, 'Não foi possível guardar a decisão.').join(' '));
    } finally {
      setBusy(false);
    }
  };

  const needsNote = (decision, item) => decision === 'reject' || (decision === 'approve' && (item.kind === 'video' || item.kind === 'embed'));

  // ── vídeo incorporado do YouTube ──
  //
  // Guarda-se o ENDEREÇO, não o vídeo: nada é descarregado nem copiado. Entra por validar e o servidor decide se o
  // endereço é do YouTube (extrai o id); um vídeo próprio aprovado continua a ser o que o atleta vê.
  const saveEmbed = async () => {
    setMessage(null);
    const url = embedUrl.trim();
    if (!url) {
      setMessage('Cole o endereço do vídeo do YouTube (ex.: https://www.youtube.com/watch?v=…).');
      return;
    }
    if (!LOOKS_LIKE_YOUTUBE.test(url)) {
      setMessage('Só se aceitam vídeos do YouTube. Cole o endereço do vídeo (youtube.com/watch?v=…, youtu.be/… ou shorts/…).');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.addEmbed(ownerId, url, embedChannel.trim() || undefined);
      // O servidor devolve o exercício inteiro: a lista de media e a demonstração em uso (com a razão).
      if (onDetail) onDetail(data); else refreshWith(data.media || []);
      setEmbedUrl('');
      setEmbedChannel('');
      toast.success('Vídeo do YouTube guardado: fica pendente até ser aprovado');
    } catch (e) {
      setMessage(errorList(e, 'Não foi possível guardar o vídeo do YouTube.').join(' '));
    } finally {
      setBusy(false);
    }
  };

  const removeEmbed = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await api.removeEmbed(ownerId);
      // A lista sem o incorporado; se houver vídeo próprio aprovado, é ele que fica (a regra não muda).
      const next = media.filter((m) => m.kind !== 'embed');
      refreshWith(next);
      // A razão de «qual está em uso» vem do servidor: pedir o exercício de novo evita um ecrã a mentir.
      if (onDetail && typeof api.get === 'function') {
        const { data } = await api.get(ownerId);
        onDetail(data);
      }
      toast.success('Vídeo do YouTube removido');
    } catch (e) {
      setMessage(errorList(e, 'Não foi possível remover o vídeo do YouTube.').join(' '));
    } finally {
      setBusy(false);
      setToRemoveEmbed(false);
    }
  };

  return (
    <div>
      {message && <div className="lib-notice bad" role="alert">{message}</div>}

      {/* Qual demonstração está em uso e PORQUÊ: a regra é uma só (o vídeo próprio aprovado manda) e o ecrã di-lo. */}
      {supportsEmbed && demo && demo.inUse ? (
        <div className="lib-notice" data-testid="demo-in-use">
          <strong>O atleta vê: {demo.inUseLabel || 'nenhuma demonstração'}.</strong> {demo.reason}
        </div>
      ) : null}

      {media.length === 0 ? (
        <div className="lib-empty" style={{ marginBottom: 12 }}>Ainda sem imagens nem vídeos.</div>
      ) : (
        <div className="lib-media">
          {media.map((item) => (
            <div key={item.id} className={`lib-media-item${item.isPrimary ? ' primary' : ''}${item.kind === 'embed' ? ' lib-embed' : ''}`}
              data-testid={item.kind === 'embed' ? `media-embed-${item.id}` : undefined}>
              {item.kind === 'video'
                ? <video src={item.url} muted loop playsInline controls preload="metadata" aria-label={item.altText || 'Demonstração em vídeo'} />
                : item.kind === 'embed'
                  // O player oficial, no próprio ecrã da gestão: é assim que a pessoa confirma o vídeo antes de o aprovar.
                  // Se não carregar, fica o endereço — nunca um quadro vazio.
                  ? (
                    <div className="lib-embed-frame">
                      <iframe src={item.url} title={item.altText ? `Vídeo do YouTube: ${item.altText}` : 'Vídeo do YouTube'}
                        loading="lazy" referrerPolicy="strict-origin-when-cross-origin"
                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen" />
                    </div>
                  )
                  : <img src={item.thumbUrl || item.url} alt={item.altText || ''} loading="lazy" decoding="async" />}
              <div className="lib-chips">
                {item.isPrimary && <span className="lib-chip accent">Principal</span>}
                {item.kind === 'video' && <span className="lib-chip">Vídeo</span>}
                {item.kind === 'embed' && <span className="lib-chip accent">Vídeo do YouTube</span>}
                {item.kind === 'embed' && item.channelName && <span className="lib-chip">{item.channelName}</span>}
                {item.origin === 'ai' && <span className="lib-chip" title="Conteúdo gerado por inteligência artificial">Gerado por IA</span>}
                {(item.origin === 'open' || item.origin === 'licensed') && <span className="lib-chip" title="Conteúdo de terceiros, com licença registada">{item.originLabel || 'Licenciado'}</span>}
                {item.validationStatus && item.validationStatus !== 'approved' && (
                  <span className={`lib-chip ${item.validationStatus === 'rejected' ? 'bad' : 'warn'}`} data-testid={`media-status-${item.id}`}
                    title="Só os ficheiros aprovados chegam aos atletas">
                    {item.validationStatus === 'rejected' ? 'Rejeitada' : 'Pendente de validação'}
                  </span>
                )}
                <span className="lib-chip">{item.source === 'catalog' ? 'Catálogo' : item.kind === 'embed' ? 'Incorporado' : 'Carregada'}</span>
              </div>
              {item.kind === 'embed' && (
                <span className="lib-hint">
                  {item.altText ? `${item.altText}. ` : ''}ID do vídeo: {item.providerVideoId || '—'}. Conteúdo de terceiros, incorporado: nada foi descarregado nem copiado.
                </span>
              )}
              {(item.validationStatus && item.validationStatus !== 'approved') && (
                <span className="lib-hint" data-testid={`media-review-${item.id}`}>
                  {item.validationStatus === 'rejected' ? 'Não é mostrada aos atletas. ' : 'Os atletas ainda não a veem: falta a validação de uma pessoa. '}
                  {item.reviewNote || ''}
                </span>
              )}
              {item.validationStatus === 'approved' && item.reviewedBy && (
                <span className="lib-hint">Validada por {item.reviewedBy}{item.reviewedAt ? ` em ${new Date(item.reviewedAt).toLocaleDateString('pt-PT')}` : ''}.</span>
              )}

              {/* Origem, licença e tamanho: o que a auditoria de licenças confere. */}
              {(item.license || item.attribution || item.sourceUrl || item.sizeBytes || item.durationMs) && (
                <details className="lib-hint" data-testid={`media-provenance-${item.id}`}>
                  <summary>Origem e licença</summary>
                  {item.license && <div>Licença: {item.license}</div>}
                  {item.attribution && <div>Atribuição: {item.attribution}</div>}
                  {item.sourceUrl && <div>Origem: <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">{item.sourceUrl}</a></div>}
                  {item.sizeBytes ? <div>Tamanho: {mb(item.sizeBytes)}{item.durationMs ? ` · ${(item.durationMs / 1000).toFixed(1).replace('.', ',')} s` : ''}</div> : null}
                </details>
              )}

              {item.kind === 'embed' ? (
                canEdit && (
                  <div className="lib-media-actions lib-embed-actions">
                    <button type="button" className="lib-btn" disabled={busy} onClick={() => setToRemoveEmbed(true)}>Remover vídeo do YouTube</button>
                  </div>
                )
              ) : (
                <>
                  {canEdit ? (
                    <input className="lib-input" defaultValue={item.altText || ''} maxLength={200} placeholder="Texto alternativo"
                      aria-label="Texto alternativo" onBlur={(e) => saveAlt(item, e.target.value)} />
                  ) : item.altText && <span className="lib-hint">{item.altText}</span>}
                </>
              )}
              {canEdit && (
                <div className="lib-media-actions">
                  {!item.isPrimary && item.kind === 'photo' && item.validationStatus !== 'rejected' && item.validationStatus !== 'pending' && (
                    <button type="button" className="lib-btn small" disabled={busy} onClick={() => setPrimary(item)}>Tornar principal</button>
                  )}
                  {supportsVideo && item.source !== 'catalog' && item.validationStatus !== 'approved' && (
                    <button type="button" className="lib-btn small" disabled={busy}
                      data-testid={item.kind === 'embed' ? `media-embed-approve-${item.id}` : undefined}
                      onClick={() => setReview({ item, decision: 'approve', note: '' })}>Aprovar</button>
                  )}
                  {supportsVideo && item.source !== 'catalog' && item.validationStatus !== 'rejected' && (
                    <button type="button" className="lib-btn small" disabled={busy} onClick={() => setReview({ item, decision: 'reject', note: '' })}>Rejeitar</button>
                  )}
                  {supportsVideo && item.source !== 'catalog' && item.validationStatus === 'approved' && item.kind === 'video' && (
                    <button type="button" className="lib-btn small" disabled={busy} onClick={() => setReview({ item, decision: 'pending', note: '' })}>Retirar aprovação</button>
                  )}
                  {supportsVideo && item.source !== 'catalog' && item.validationStatus === 'approved' && item.kind === 'embed' && (
                    <button type="button" className="lib-btn small" disabled={busy} onClick={() => setReview({ item, decision: 'pending', note: '' })}>Retirar aprovação</button>
                  )}
                  {item.kind !== 'embed' && (
                    <button type="button" className="lib-btn small danger" disabled={busy || item.source === 'catalog'}
                      title={item.source === 'catalog' ? 'Os ficheiros do catálogo não se apagam: carregue outro e marque-o como principal.' : undefined}
                      onClick={() => setToDelete(item)}>Apagar</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="lib-field" style={{ padding: 10, border: '1px dashed var(--border-color)', borderRadius: 'var(--radius-md)' }}>
          <span className="lib-label">{supportsVideo ? 'Carregar imagem ou vídeo' : 'Carregar imagem'}</span>
          <input className="lib-input" value={alt} maxLength={200} placeholder="Texto alternativo (opcional)"
            aria-label="Texto alternativo do novo ficheiro" onChange={(e) => setAlt(e.target.value)} disabled={busy || full} />
          <label className="lib-check">
            <input type="checkbox" checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} disabled={busy || full} />
            Usar como imagem principal (só imagens)
          </label>

          {supportsVideo && (
            <>
              <label className="lib-label" htmlFor="media-origin">Origem</label>
              <select id="media-origin" className="lib-input" value={origin} onChange={(e) => setOrigin(e.target.value)} disabled={busy || full}>
                {ORIGINS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              {(thirdParty || showProvenance || license || attribution || sourceUrl) && (
                <>
                  <input className="lib-input" value={license} maxLength={80} placeholder={`Licença${thirdParty ? ' (obrigatória)' : ''}, ex.: CC BY 4.0`}
                    aria-label="Licença" onChange={(e) => setLicense(e.target.value)} disabled={busy || full} />
                  <input className="lib-input" value={attribution} maxLength={200} placeholder={`Atribuição${thirdParty ? ' (obrigatória)' : ''}, ex.: © Autor`}
                    aria-label="Atribuição" onChange={(e) => setAttribution(e.target.value)} disabled={busy || full} />
                  <input className="lib-input" value={sourceUrl} maxLength={500} placeholder={`Endereço de origem${thirdParty ? ' (obrigatório, https)' : ''}`}
                    aria-label="Endereço de origem" onChange={(e) => setSourceUrl(e.target.value)} disabled={busy || full} />
                </>
              )}
              {!thirdParty && !showProvenance && !license && !attribution && !sourceUrl && (
                <button type="button" className="lib-btn small" onClick={() => setShowProvenance(true)}>Indicar licença e atribuição</button>
              )}
            </>
          )}

          <input ref={fileRef} type="file" accept={supportsVideo ? ACCEPT_ALL : ACCEPT_IMAGES} className="lib-input" disabled={busy || full}
            aria-label="Escolher ficheiro" onChange={(e) => upload(e.target.files?.[0])} />
          <span className="lib-hint">
            {full
              ? `Já tem o máximo de ${maxPerItem} ficheiros.`
              : `JPG, PNG, WEBP ou GIF até ${Math.floor(maxBytes / 1024 / 1024)} MB${supportsVideo ? `; vídeo MP4 (H.264) até 30 MB ou WEBM até 15 MB, com ${MAX_VIDEO_SECONDS} s no máximo, ${MIN_VIDEO_HEIGHT} px de altura e sem som de preferência (grave de lado, com o movimento certo). Um vídeo só chega aos atletas depois de aprovado.` : '.'} Os ficheiros ficam guardados neste ginásio.`}
          </span>
          {busy && (
            <span className="lib-hint" role="status" data-testid="media-upload-progress">
              {progress == null ? 'A enviar…' : `A enviar… ${progress}%`}
            </span>
          )}
        </div>
      )}

      {/* A segunda forma de demonstração: o vídeo do YouTube, incorporado. Só o endereço — nada é descarregado. */}
      {canEdit && supportsEmbed && (
        <div className="lib-field" style={{ padding: 10, border: '1px dashed var(--border-color)', borderRadius: 'var(--radius-md)', marginTop: 12 }}>
          <label className="lib-label" htmlFor="embed-url">Vídeo do YouTube (incorporado)</label>
          <input id="embed-url" className="lib-input" value={embedUrl} maxLength={500} disabled={busy}
            placeholder="https://www.youtube.com/watch?v=… ou https://youtu.be/…"
            aria-describedby="embed-hint" onChange={(e) => setEmbedUrl(e.target.value)} />
          <input className="lib-input" value={embedChannel} maxLength={120} disabled={busy}
            placeholder="Canal ou autor (opcional: vem do YouTube quando é possível)" aria-label="Canal do vídeo do YouTube"
            onChange={(e) => setEmbedChannel(e.target.value)} />
          <span className="lib-hint" id="embed-hint" data-testid="embed-hint">
            Só endereços do YouTube. Fica pendente até uma pessoa o aprovar, e o vídeo do YouTube nunca substitui um vídeo
            nosso aprovado. Nada é descarregado nem copiado: o atleta vê o player oficial do YouTube.
          </span>
          <div className="lib-media-actions lib-embed-actions">
            <button type="button" className="lib-btn primary" disabled={busy || !embedUrl.trim()} data-testid="embed-save" onClick={saveEmbed}>
              {embed ? 'Substituir pelo vídeo do YouTube' : 'Guardar vídeo do YouTube'}
            </button>
            {embed && (
              <button type="button" className="lib-btn" disabled={busy} onClick={() => setToRemoveEmbed(true)}>Remover o do YouTube</button>
            )}
          </div>
        </div>
      )}

      {review && (
        <div className="lib-overlay" role="dialog" aria-modal="true" aria-label="Decisão sobre o ficheiro">
          <div className="lib-dialog confirm" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>
              {review.decision === 'approve' ? 'Aprovar' : review.decision === 'reject' ? 'Rejeitar' : 'Retirar a aprovação'}
            </h3>
            <p className="lib-hint">
              {review.decision === 'approve'
                ? (review.item.kind === 'embed'
                  ? 'Ao aprovar, o vídeo do YouTube passa a aparecer aos atletas — mas só quando não houver um vídeo nosso aprovado. Fica registado o seu nome e a data.'
                  : 'Ao aprovar, o ficheiro passa a aparecer aos atletas. Fica registado o seu nome e a data.')
                : review.decision === 'reject' ? 'O ficheiro deixa de ser mostrado aos atletas. Indique o motivo.' : 'O ficheiro deixa de ser mostrado aos atletas até nova decisão.'}
            </p>
            <label className="lib-label" htmlFor="review-note">
              {needsNote(review.decision, review.item) ? 'Nota (obrigatória)' : 'Nota (opcional)'}
            </label>
            <textarea id="review-note" className="lib-input" rows={3} maxLength={500} value={review.note}
              placeholder={review.decision === 'approve' && review.item.kind === 'video'
                ? 'O que verificou: movimento correto, enquadramento, sem som, ciclo limpo…'
                : review.decision === 'approve' && review.item.kind === 'embed'
                  ? 'O que verificou: abriu o endereço, o canal é o indicado e o movimento é o do exercício…'
                  : ''}
              onChange={(e) => setReview({ ...review, note: e.target.value })} />
            <div className="lib-media-actions" style={{ marginTop: 12 }}>
              <button type="button" className="lib-btn" onClick={() => setReview(null)} disabled={busy}>Cancelar</button>
              <button type="button" className={`lib-btn primary${review.decision === 'reject' ? ' danger' : ''}`} onClick={submitReview}
                disabled={busy || (needsNote(review.decision, review.item) && review.note.trim().length < (review.decision === 'approve' ? 10 : 3))}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {toDelete && (
        <ConfirmDialog title="Apagar ficheiro" message="O ficheiro (e a miniatura) são apagados. Esta ação não se desfaz." confirmLabel="Apagar" danger busy={busy}
          onConfirm={remove} onCancel={() => setToDelete(null)} />
      )}

      {toRemoveEmbed && (
        <ConfirmDialog title="Remover o vídeo do YouTube"
          message="O exercício deixa de ter vídeo incorporado. Se houver um vídeo nosso aprovado, é esse que o atleta passa a ver. Nada é apagado do YouTube — o vídeo não é nosso."
          confirmLabel="Remover" busy={busy}
          onConfirm={removeEmbed} onCancel={() => setToRemoveEmbed(false)} />
      )}
    </div>
  );
}
